import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { switchMap, tap, filter, map } from 'rxjs/operators';

import { SyncStatus } from '../data/reducer';
import { SelectSyncStatus } from '../data/selectors';
import { ForceService } from '../../forces/force.service';
import { ForceSyncService } from './sync.force';
import { BaseSyncService, UserDataSyncStatus } from './sync.base';

/**
 * A fully self-contained sync button component that displays sync status and triggers sync on click.
 *
 * For syncing the current force (default):
 * ```html
 * <abs-sync-button></abs-sync-button>
 * ```
 *
 * For syncing all forces:
 * ```html
 * <abs-sync-button mode="all"></abs-sync-button>
 * ```
 *
 * For syncing with a custom sync service (e.g., settlements):
 * ```html
 * <abs-sync-button mode="all" [customSyncService]="settlementSyncService"></abs-sync-button>
 * ```
 *
 * The component automatically:
 * - In "force" mode: Gets the current force from ForceService and syncs it
 * - In "all" mode: Syncs all forces via ForceSyncService.sync() (or customSyncService if provided)
 *
 * States:
 * - Clean: Green cloud-done icon - "Synced"
 * - Dirty: Cloud icon - "Unsaved changes"
 * - InProgress: Spinner - "Syncing..." (button disabled)
 * - Error: Red cloud-offline icon - "Sync failed"
 */
@Component({
    standalone: false,
    selector: 'abs-sync-button',
    template: `
        @if (style === 'listItem') {
            <ion-item [class]="syncStatus" (click)="onSyncClick()" [button]="true" [disabled]="syncStatus === 'InProgress'">
                <abs-sync-status-icon [status]="syncStatus" [showClean]="true" [color]="iconColor"> </abs-sync-status-icon>
                {{ label }}
            </ion-item>
        } @else if (style === 'button') {
            <ion-button (click)="onSyncClick()" [disabled]="syncStatus === 'InProgress'" title="{{ label }}">
                <abs-sync-status-icon [status]="syncStatus" [showClean]="true" [color]="iconColor"> </abs-sync-status-icon>
            </ion-button>
        }
    `,
    styles: [
        `
            :host {
                display: block;
            }

            ion-item {
                --background: white;
                --inner-padding-end: 0;
            }

            ion-item abs-sync-status-icon {
                margin-right: var(--gutter);
            }

            ion-item.Error {
                color: var(--ion-color-danger, #eb445a);
            }

            ion-item.InProgress {
                color: var(--ion-color-primary, #3880ff);
            }
        `
    ]
})
export class SyncButtonComponent implements OnInit, OnDestroy {
    /**
     * The sync mode:
     * - "force" (default): Syncs the current force from ForceService
     * - "all": Syncs all forces via ForceSyncService.sync() (or customSyncService if provided)
     */
    @Input() mode: 'force' | 'all' = 'force';

    @Input() style: 'listItem' | 'button' = 'button';

    /**
     * Override color for the sync status icon. When set, this color is applied to all status icons
     * instead of the default status-specific colors. Useful for buttons with colored backgrounds.
     */
    @Input() iconColor?: string;

    /**
     * Optional custom sync service (e.g., SettlementSyncService).
     * If provided, this will be used instead of ForceSyncService for sync operations.
     * The custom service must extend BaseSyncService and have a syncStatus$ observable.
     */
    @Input() customSyncService?: BaseSyncService & { syncStatus$?: any };

    syncStatus: SyncStatus = 'Clean';
    private subscription: Subscription;

    constructor(
        private store: Store<any>,
        private forceService: ForceService,
        private forceSyncService: ForceSyncService
    ) {}

    ngOnInit() {
        if (this.mode === 'all') {
            // Subscribe to the global sync status from the sync service (custom or force)
            const syncService = this.customSyncService || this.forceSyncService;
            this.subscription = syncService.syncStatus$
                .pipe(
                    map((status: UserDataSyncStatus) => this.mapGlobalStatusToSyncStatus(status.status)),
                    tap((status: SyncStatus) => (this.syncStatus = status))
                )
                .subscribe();
        } else {
            // Subscribe to the current force's sync status
            this.subscription = this.forceService.force$
                .pipe(
                    filter((f) => !!f),
                    switchMap((f) => this.store.select(SelectSyncStatus, { entityType: 'forces', id: f.id })),
                    tap((status) => (this.syncStatus = status))
                )
                .subscribe();
        }
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    private mapGlobalStatusToSyncStatus(status: 'SYNCING' | 'IDLE' | 'ERROR'): SyncStatus {
        switch (status) {
            case 'SYNCING':
                return 'InProgress';
            case 'ERROR':
                return 'Error';
            case 'IDLE':
            default:
                return 'Clean';
        }
    }

    get label(): string {
        switch (this.syncStatus) {
            case 'Clean':
                return this.mode === 'all' ? 'All synced' : 'Synced';
            case 'Dirty':
                return 'Unsaved changes';
            case 'InProgress':
                return 'Syncing...';
            case 'Error':
                return 'Sync failed';
            default:
                return '';
        }
    }

    onSyncClick() {
        if (this.syncStatus !== 'InProgress') {
            if (this.mode === 'all') {
                // Manual sync - always show visual feedback
                const syncService = this.customSyncService || this.forceSyncService;
                syncService.sync({ manual: true });
            } else {
                const force = this.forceService.force();
                if (force) {
                    this.forceSyncService.syncForce(force);
                }
            }
        }
    }
}
